import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { API_BASE_URL } from '../../config';
import { Modal } from '../ui/Modal.js';
import LoadingSpinner from '../LoadingSpinner';

const getQuestionType = (type) => {
  switch (type) {
    case 1: return "Choix unique";
    case 2: return "Numérique";
    case 3: return "Texte court";
    case 4: return "Distribution";
    case 5: return "Choix multiple";
    case 6: return "Texte long";
    case 7: return "Fichier";
    case 8: return "Upload";
    case 9: return "Titre";
    default: return "Autre";
  }
};

const isMappableQuestion = (type) => {
  return type === 1 || type === 2 || type === 5; // Choix unique, Numérique, Choix multiple
};

const getValueDisplayClass = (value) => {
  if (value === 'true') return 'text-green-600 dark:text-green-400 font-medium';
  if (value === 'false') return 'text-red-600 dark:text-red-400 font-medium';
  if (value === 'null') return 'text-gray-500 dark:text-gray-400 font-medium italic';
  return '';
};

function GIFormMapper({ selectedCampaign, formData, fetchWithAuth, isOpen, onClose, onMappingChange }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [schemas, setSchemas] = useState({});
  const [brands, setBrands] = useState([]);
  const [existingMapping, setExistingMapping] = useState(null);
  const [mappings, setMappings] = useState({});

  const schemaOptions = Object.keys(schemas).map(schema => ({
    value: schema,
    label: schema.replace(/bi_gi_/g, '').replace(/[a-z]/g, match => match.toUpperCase())
  }));

  const form = formData.data.survey.JsonContent || [];

  // Extract all questions from form data
  const allQuestions = form || [];

  // Extract mappable questions (numeric, single choice, multiple choice)
  const mappableQuestions = form.filter(
    element => element.type === 1 || element.type === 2 || element.type === 5
  ) || [];

  // Legacy support for numeric questions only
  const numericQuestions = form.filter(
    element => element.type === 2
  ) || [];

  useEffect(() => {
    if (isOpen && selectedCampaign && formData) {
      fetchInitialData();
    }
  }, [isOpen, selectedCampaign, formData]);

  const fetchInitialData = async () => {
    setLoading(true);

    try {
      // Fetch table configurations
      const tablesResponse = await fetchWithAuth(`${API_BASE_URL}/api/dwh/gi/dwh-facts-mapping`);
      const tablesData = await tablesResponse.json();
      setSchemas(tablesData.schemas || {});

      // Fetch brands for the client
      const brandsResponse = await fetchWithAuth(
        `${API_BASE_URL}/api/dwh/gi/brands?client_id=${selectedCampaign.data.ClientId}`
      );
      const brandsData = await brandsResponse.json();
      setBrands(brandsData.brands || []);

      // Fetch existing mapping
      const mappingResponse = await fetchWithAuth(
        `${API_BASE_URL}/api/dwh/gi/${formData.data.survey.Id}/dwh-mapping`
      );
      if (mappingResponse.ok) {
        const mappingData = await mappingResponse.json();
        if (mappingData.mapping && mappingData.mapping.length > 0) {
          setExistingMapping(mappingData.mapping[0]);
          setMappings(mappingData.mapping[0].mapping?.mappings || {});
        }
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (questionId, field, value) => {
    setMappings(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [field]: value
      }
    }));
  };

  console.log('>>> mappings', mappings);

  const toggleQuestionMapping = (questionId, enabled) => {
    if (enabled) {
      const question = allQuestions.find(q => (q.Id || q.name) === questionId);
      const baseMapping = {
        brand_id: '',
        column_name: '',
        validation_rules: '',
        is_active: true
      };

      // For choice questions, initialize choice mappings with default values
      if (question && (question.type === 1 || question.type === 5) && question.options) {
        baseMapping.choice_mappings = {};
        question.options.forEach((option, index) => {
          const optionKey = option.value || index;
          // Initialize with the original option value as default
          baseMapping.choice_mappings[optionKey] = option.label || option.text || option.value || '';
        });
      }

      setMappings(prev => ({
        ...prev,
        [questionId]: baseMapping
      }));
    } else {
      setMappings(prev => ({
        ...prev,
        [questionId]: {
          ...prev[questionId],
          is_active: false
        }
      }));
    }
  };

  // Fonction pour convertir les valeurs spéciales string vers leur type approprié
  const convertSpecialValues = (obj) => {
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(convertSpecialValues);
    }

    const converted = {};
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'string') {
        if (value === 'true') {
          converted[key] = true;
        } else if (value === 'false') {
          converted[key] = false;
        } else if (value === 'null') {
          converted[key] = null;
        } else {
          converted[key] = value;
        }
      } else if (typeof value === 'object') {
        converted[key] = convertSpecialValues(value);
      } else {
        converted[key] = value;
      }
    }
    return converted;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only save active mappings
      const activeMappings = Object.fromEntries(
        Object.entries(mappings).filter(([_, mapping]) => mapping.is_active)
      );

      // Validate that all active mappings have required fields
      const invalidMappings = Object.entries(activeMappings).filter(([questionId, mapping]) => {
        return !mapping.brand_id || !mapping.column_name;
      });

      if (invalidMappings.length > 0) {
        alert(`Veuillez compléter tous les champs requis (Marque et Colonne) pour les questions mappées.`);
        setSaving(false);
        return;
      }

      // Convertir les valeurs spéciales avant sauvegarde
      const convertedMappings = convertSpecialValues(activeMappings);

      const mappingData = {
        mappings: convertedMappings
      };

      if (existingMapping) {
        // Update existing mapping
        await fetchWithAuth(
          `${API_BASE_URL}/api/dwh/gi/${formData.data.survey.Id}/dwh-mapping/${existingMapping.id}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ mapping: mappingData }),
          }
        );
      } else {
        // Create new mapping
        await fetchWithAuth(
          `${API_BASE_URL}/api/dwh/gi/${formData.data.survey.Id}/dwh-mapping`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              form_id: formData.data.survey.Id,
              campaign_id: selectedCampaign.value,
              client_id: selectedCampaign.data.ClientId,
              mapping: mappingData
            }),
          }
        );
      }

      // Appeler la callback pour rafraîchir la liste des formulaires mappés
      if (onMappingChange) {
        onMappingChange();
      }

      onClose();
    } catch (error) {
      console.error('Error saving mapping:', error);
    } finally {
      setSaving(false);
    }
  };

  const brandOptions = brands.map(brand => ({
    value: brand.Id,
    label: brand.Name
  }));

  const getColumnOptions = (tableName) => {
    const table = schemas[tableName];

    if (!table) return [];

    return Object.entries(table.columns).filter(([_, column]) => column.user_mappable).map(([columnName, column]) => ({
      value: columnName,
      label: columnName
    }));
  };

  const isMapped = (questionId) => {
    return mappings[questionId] &&
           mappings[questionId].is_active &&
           mappings[questionId].brand_id &&
           mappings[questionId].column_name;
  };

  const isActive = (questionId) => {
    return mappings[questionId] && mappings[questionId].is_active;
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Gestion du Mapping DWH" size="xl" fullHeight={true}>
            <div
        className="p-4 overflow-y-auto"
        style={{ maxHeight: 'calc(100vh - 120px)' }}
      >
        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Header info */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <h3 className="text-base font-semibold text-blue-800 dark:text-blue-200">
                Configuration du mapping pour: {selectedCampaign.data.CampaignName}
              </h3>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-2 mb-4">
              <div className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 py-1 px-3 rounded-full text-xs">
                {Object.values(mappings).filter(m => m.is_active).length} question{Object.values(mappings).filter(m => m.is_active).length > 1 ? 's' : ''} mappée{Object.values(mappings).filter(m => m.is_active).length > 1 ? 's' : ''}
              </div>
              <div className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 py-1 px-3 rounded-full text-xs">
                {mappableQuestions.length} question{mappableQuestions.length > 1 ? 's' : ''} mappable{mappableQuestions.length > 1 ? 's' : ''}
              </div>
              <div className="bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 py-1 px-3 rounded-full text-xs">
                {numericQuestions.length} numérique{numericQuestions.length > 1 ? 's' : ''}
              </div>
              <div className="bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 py-1 px-3 rounded-full text-xs">
                {form.filter(q => q.type === 1).length} choix unique{form.filter(q => q.type === 1).length > 1 ? 's' : ''}
              </div>
              <div className="bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 py-1 px-3 rounded-full text-xs">
                {form.filter(q => q.type === 5).length} choix multiple{form.filter(q => q.type === 5).length > 1 ? 's' : ''}
              </div>
              <div className="bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 py-1 px-3 rounded-full text-xs">
                {allQuestions.length} question{allQuestions.length > 1 ? 's' : ''} au total
              </div>
            </div>

            {/* Questions list */}
            <div className="space-y-2">
              {allQuestions.map((question) => {
                const questionId = question.Id || question.name;
                const questionMapped = isMapped(questionId);
                const questionMapping = mappings[questionId] || {};
                const isMappableType = isMappableQuestion(question.type);
                const questionType = getQuestionType(question.type);

                return (
                    <div
                      key={questionId}
                      className={`border rounded-md p-3 ${
                        questionMapped
                          ? 'border-green-300 bg-green-50 dark:bg-green-900/20 dark:border-green-700'
                          : isMappableType
                          ? 'border-blue-300 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700'
                          : 'border-gray-300 bg-gray-50 dark:bg-gray-800 dark:border-gray-600'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                              {question.label || question.name}
                            </h4>
                            <span className={`px-1.5 py-0.5 text-xs rounded-full flex-shrink-0 ${
                              question.type === 2
                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                                : question.type === 1
                                ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                                : question.type === 5
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}>
                              {questionType}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            ID: {questionId}
                          </p>
                          {question.question && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                              {question.question}
                            </p>
                          )}
                          {/* Show available choices for single/multiple choice questions */}
                          {(question.type === 1 || question.type === 5) && question.options && question.options.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                Choix disponibles:
                              </p>
                              <div className="flex flex-wrap gap-1">
                                {question.options.slice(0, 3).map((option, idx) => (
                                  <span key={idx} className="inline-block bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-2 py-1 rounded text-xs">
                                    {option.label || option.text || option.value || `Option ${idx + 1}`}
                                  </span>
                                ))}
                                {question.options.length > 3 && (
                                  <span className="inline-block text-gray-500 dark:text-gray-400 px-2 py-1 text-xs">
                                    +{question.options.length - 3} autres
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center ml-3 flex-shrink-0">
                          {isMappableType ? (
                            <label className="inline-flex items-center cursor-pointer">
                              <div className="relative">
                                <input
                                  type="checkbox"
                                  className="sr-only"
                                  checked={isActive(questionId)}
                                  onChange={(e) => toggleQuestionMapping(questionId, e.target.checked)}
                                />
                                <div className={`block w-8 h-4 rounded-full transition-colors ${
                                  isActive(questionId)
                                    ? 'bg-blue-600'
                                    : 'bg-gray-300 dark:bg-gray-600'
                                }`}></div>
                                <div className={`absolute left-0 top-0 bg-white w-4 h-4 rounded-full transition-transform shadow-sm ${
                                  isActive(questionId)
                                    ? 'transform translate-x-4'
                                    : 'transform translate-x-0'
                                }`}></div>
                              </div>
                              <span className="ml-2 text-xs text-gray-700 dark:text-gray-300">
                                Mapper
                              </span>
                            </label>
                          ) : (
                            <span className="text-xs text-gray-500 dark:text-gray-400 italic">
                              Non mappable
                            </span>
                          )}
                        </div>
                      </div>

                      {isActive(questionId) && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                            {/* Table selection - for now only one option */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Table de destination
                              </label>
                              <Select
                                value={schemaOptions.find(option => option.value.toString() === questionMapping.table_name?.toString())}
                                onChange={(selectedOption) => {
                                  handleMappingChange(questionId, 'table_name', selectedOption?.value || '')
                                  handleMappingChange(questionId, 'column_name', '')
                                }}
                                options={schemaOptions}
                                placeholder="Sélectionner..."
                                className="react-select-container text-xs"
                                classNamePrefix="react-select"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{
                                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                  menu: (base) => ({ ...base, zIndex: 9999 })
                                }}
                              />
                            </div>

                            {/* Brand selection (required column) */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Marque <span className="text-red-500">*</span>
                              </label>
                              <Select
                                value={brandOptions.find(option => option.value.toString() === questionMapping.brand_id?.toString())}
                                onChange={(selectedOption) =>
                                  handleMappingChange(questionId, 'brand_id', selectedOption?.value || '')
                                }
                                options={brandOptions}
                                placeholder="Sélectionner..."
                                className="react-select-container text-xs"
                                classNamePrefix="react-select"
                                isClearable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{
                                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                  menu: (base) => ({ ...base, zIndex: 9999 })
                                }}
                              />
                            </div>

                            {/* Column mapping */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Colonne <span className="text-red-500">*</span>
                              </label>
                              <Select
                                value={questionMapping.column_name ? getColumnOptions(questionMapping.table_name).find(option => option.value.toString() === questionMapping.column_name?.toString()) : null}
                                onChange={(selectedOption) =>
                                  handleMappingChange(questionId, 'column_name', selectedOption?.value || '')
                                }
                                options={getColumnOptions(questionMapping.table_name)}
                                placeholder="Sélectionner..."
                                className="react-select-container text-xs"
                                classNamePrefix="react-select"
                                isClearable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{
                                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                  menu: (base) => ({ ...base, zIndex: 9999 })
                                }}
                              />
                            </div>
                          </div>

                          {/* Choice mapping section for single/multiple choice questions */}
                          {(question.type === 1 || question.type === 5) && question.options && question.options.length > 0 && (
                            <div className="mb-4">
                              <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Mapping des choix vers valeurs DWH
                              </h5>
                              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                                <div className="space-y-2">
                                  {question.options.map((option, optionIndex) => {
                                    const optionKey = option.value || optionIndex;
                                    const currentValue = questionMapping.choice_mappings?.[optionKey] || '';

                                    return (
                                      <div key={optionIndex} className="flex items-center gap-3">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2">
                                            <span className="text-xs text-gray-600 dark:text-gray-400">
                                              {option.label || option.text || option.value || `Option ${optionIndex + 1}`}
                                            </span>
                                            {currentValue && (
                                              <span className="text-xs text-gray-400">
                                                →
                                              </span>
                                            )}
                                            {currentValue === 'true' && (
                                              <span className="px-1 py-0.5 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded text-xs font-mono">
                                                true
                                              </span>
                                            )}
                                            {currentValue === 'false' && (
                                              <span className="px-1 py-0.5 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded text-xs font-mono">
                                                false
                                              </span>
                                            )}
                                            {currentValue === 'null' && (
                                              <span className="px-1 py-0.5 bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300 rounded text-xs font-mono italic">
                                                null
                                              </span>
                                            )}
                                            {currentValue && !['true', 'false', 'null'].includes(currentValue) && (
                                              <span className="px-1 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded text-xs font-mono">
                                                "{currentValue}"
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                        <div className="flex-1">
                                          <div className="flex gap-2">
                                            <input
                                              type="text"
                                              placeholder="Valeur mappée dans le DWH"
                                              value={currentValue}
                                              onChange={(e) => {
                                                const choiceMappings = questionMapping.choice_mappings || {};
                                                choiceMappings[optionKey] = e.target.value;
                                                handleMappingChange(questionId, 'choice_mappings', choiceMappings);
                                              }}
                                              className={`flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100 ${getValueDisplayClass(currentValue)}`}
                                            />
                                            <div className="flex gap-1">
                                              {/* Quick action buttons for special values */}
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const choiceMappings = questionMapping.choice_mappings || {};
                                                  choiceMappings[optionKey] = 'true';
                                                  handleMappingChange(questionId, 'choice_mappings', choiceMappings);
                                                }}
                                                className="px-2 py-1 text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded hover:bg-green-200 dark:hover:bg-green-800 transition-colors"
                                                title="Mapper vers true"
                                              >
                                                T
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const choiceMappings = questionMapping.choice_mappings || {};
                                                  choiceMappings[optionKey] = 'false';
                                                  handleMappingChange(questionId, 'choice_mappings', choiceMappings);
                                                }}
                                                className="px-2 py-1 text-xs bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-800 transition-colors"
                                                title="Mapper vers false"
                                              >
                                                F
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const choiceMappings = questionMapping.choice_mappings || {};
                                                  choiceMappings[optionKey] = 'null';
                                                  handleMappingChange(questionId, 'choice_mappings', choiceMappings);
                                                }}
                                                className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
                                                title="Mapper vers null"
                                              >
                                                ∅
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                                                <div className="mt-3 space-y-1 text-xs text-gray-500 dark:text-gray-400">
                                  <div className="flex items-center gap-2">
                                    <span>💡</span>
                                    <span>Définissez comment chaque choix sera stocké dans le DWH.</span>
                                  </div>
                                  <div className="pl-5 space-y-1">
                                    <div className="flex items-center gap-2">
                                      <span className="px-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded font-mono">T</span>
                                      <span>= true (booléen)</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="px-1 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded font-mono">F</span>
                                      <span>= false (booléen)</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="px-1 bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300 rounded font-mono">∅</span>
                                      <span>= null (valeur vide)</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-400">• Laissez vide pour utiliser la valeur originale</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-400">• Saisissez du texte pour une valeur personnalisée</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Validation rules */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Règles de validation (optionnel)
                            </label>
                            <textarea
                              value={questionMapping.validation_rules || ''}
                              onChange={(e) =>
                                handleMappingChange(questionId, 'validation_rules', e.target.value)
                              }
                              placeholder="Ex: min=0, max=1000, required=true"
                              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                              rows={2}
                            />
                          </div>
                        </div>
                      )}
                  </div>
                );
              })}
            </div>

            {allQuestions.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <p>Aucune question trouvée dans ce formulaire.</p>
              </div>
            )}

            {allQuestions.length > 0 && mappableQuestions.length === 0 && (
              <div className="text-center py-8 text-yellow-600 dark:text-yellow-400">
                <p>Aucune question mappable trouvée dans ce formulaire.</p>
                <p className="text-sm mt-2">
                  Seules les questions de type "Numérique", "Choix unique" et "Choix multiple" peuvent être mappées vers le DWH.
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-2 pt-4 mt-4 border-t border-gray-200 dark:border-gray-600 sticky bottom-0 bg-white dark:bg-gray-800">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={saving}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-3 py-2 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-white mr-2"></div>
                    Sauvegarde...
                  </div>
                ) : (
                  'Sauvegarder'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default GIFormMapper;